import { useEffect, useRef, useState } from 'react'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Link2, Link2Off, Code as CodeIcon,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Heading1, Heading2, Heading3, RemoveFormatting, Pilcrow,
} from 'lucide-react'
import type { NotepadBlock, Block } from './types'

// Rich-text editor for the `notepad` block. Uses contentEditable +
// document.execCommand under the hood. execCommand is deprecated but
// still implemented and fully synchronous in every modern browser, which
// keeps the implementation tiny and dependency-free. We sync the
// resulting innerHTML back to the block model on every input.

interface Props {
  block: NotepadBlock
  onChange: (b: Block) => void
  onSelect: () => void
}

export default function NotepadEditor({ block, onChange, onSelect }: Props) {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const [, force] = useState(0)

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    const incoming = block.html ?? ''
    if (el.innerHTML !== incoming) el.innerHTML = incoming
  }, [block.html])

  const exec = (command: string, arg?: string) => {
    editorRef.current?.focus()
    try { document.execCommand(command, false, arg) } catch { /* swallow */ }
    pushHtml()
    force(n => n + 1)
  }

  const pushHtml = () => {
    const el = editorRef.current
    if (!el) return
    const html = el.innerHTML
    if (html === (block.html ?? '')) return
    onChange({ ...block, html, text: el.innerText })
  }

  const isActive = (cmd: string): boolean => {
    try { return document.queryCommandState(cmd) } catch { return false }
  }

  const formatBlock = (tag: string) => exec('formatBlock', tag)

  const insertLink = () => {
    const url = window.prompt('Link URL', 'https://')
    if (!url) return
    exec('createLink', url)
  }

  const minHeight = block.minHeight ?? 160

  return (
    <div
      className="notepad-block"
      onClick={onSelect}
      style={{
        border: '1px solid var(--pg-border)',
        borderRadius: 8,
        background: 'var(--pg-surface)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          padding: 6,
          borderBottom: '1px solid var(--pg-border)',
          background: 'var(--pg-bg)',
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <ToolGroup>
          <ToolButton title="Bold (Ctrl+B)" active={isActive('bold')} onClick={() => exec('bold')}><Bold size={14} /></ToolButton>
          <ToolButton title="Italic (Ctrl+I)" active={isActive('italic')} onClick={() => exec('italic')}><Italic size={14} /></ToolButton>
          <ToolButton title="Underline (Ctrl+U)" active={isActive('underline')} onClick={() => exec('underline')}><UnderlineIcon size={14} /></ToolButton>
          <ToolButton title="Strikethrough" active={isActive('strikeThrough')} onClick={() => exec('strikeThrough')}><Strikethrough size={14} /></ToolButton>
        </ToolGroup>

        <Sep />

        <ToolGroup>
          <ToolButton title="Paragraph" onClick={() => formatBlock('P')}><Pilcrow size={14} /></ToolButton>
          <ToolButton title="Heading 1" onClick={() => formatBlock('H1')}><Heading1 size={14} /></ToolButton>
          <ToolButton title="Heading 2" onClick={() => formatBlock('H2')}><Heading2 size={14} /></ToolButton>
          <ToolButton title="Heading 3" onClick={() => formatBlock('H3')}><Heading3 size={14} /></ToolButton>
        </ToolGroup>

        <Sep />

        <ToolGroup>
          <ToolButton title="Bulleted list" active={isActive('insertUnorderedList')} onClick={() => exec('insertUnorderedList')}><List size={14} /></ToolButton>
          <ToolButton title="Numbered list" active={isActive('insertOrderedList')} onClick={() => exec('insertOrderedList')}><ListOrdered size={14} /></ToolButton>
          <ToolButton title="Block quote" onClick={() => formatBlock('BLOCKQUOTE')}><Quote size={14} /></ToolButton>
          <ToolButton title="Inline code" onClick={() => exec('formatBlock', 'PRE')}><CodeIcon size={14} /></ToolButton>
        </ToolGroup>

        <Sep />

        <ToolGroup>
          <ToolButton title="Align left"   active={isActive('justifyLeft')}   onClick={() => exec('justifyLeft')}><AlignLeft size={14} /></ToolButton>
          <ToolButton title="Align center" active={isActive('justifyCenter')} onClick={() => exec('justifyCenter')}><AlignCenter size={14} /></ToolButton>
          <ToolButton title="Align right"  active={isActive('justifyRight')}  onClick={() => exec('justifyRight')}><AlignRight size={14} /></ToolButton>
          <ToolButton title="Justify"      active={isActive('justifyFull')}   onClick={() => exec('justifyFull')}><AlignJustify size={14} /></ToolButton>
        </ToolGroup>

        <Sep />

        <ToolGroup>
          <ToolButton title="Add link" onClick={insertLink}><Link2 size={14} /></ToolButton>
          <ToolButton title="Remove link" onClick={() => exec('unlink')}><Link2Off size={14} /></ToolButton>
        </ToolGroup>

        <Sep />

        <ToolGroup>
          <label
            title="Text color"
            style={{ ...toolBtnBaseStyle(false), padding: 0, position: 'relative' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 6px' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>A</span>
              <span style={{ width: 10, height: 10, border: '1px solid var(--pg-border)', borderRadius: 2, background: 'currentColor' }} />
            </span>
            <input
              type="color"
              onChange={(e) => exec('foreColor', e.target.value)}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
            />
          </label>
          <label
            title="Highlight color"
            style={{ ...toolBtnBaseStyle(false), padding: 0, position: 'relative' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 6px' }}>
              <span style={{ fontWeight: 700, fontSize: 13, background: '#fde047', padding: '0 2px' }}>H</span>
            </span>
            <input
              type="color"
              onChange={(e) => exec('hiliteColor', e.target.value)}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
            />
          </label>
        </ToolGroup>

        <Sep />

        <ToolGroup>
          <ToolButton title="Clear formatting" onClick={() => exec('removeFormat')}><RemoveFormatting size={14} /></ToolButton>
        </ToolGroup>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={pushHtml}
        onBlur={pushHtml}
        onFocus={onSelect}
        onKeyUp={() => force(n => n + 1)}
        onMouseUp={() => force(n => n + 1)}
        onPaste={(e) => {
          // Paste as plain text to avoid carrying styles from the source
          // document — keeps the notepad's formatting predictable.
          e.preventDefault()
          const text = e.clipboardData.getData('text/plain')
          document.execCommand('insertText', false, text)
        }}
        data-placeholder="Start writing…"
        className="notepad-editor"
        style={{
          padding: '12px 14px',
          minHeight,
          outline: 'none',
          fontSize: '1.0125rem',
          lineHeight: 1.55,
          color: 'var(--pg-text)',
        }}
      />

      <style>{`
        .notepad-editor:empty:before {
          content: attr(data-placeholder);
          color: var(--pg-text-secondary);
          opacity: 0.6;
          pointer-events: none;
        }
        .notepad-editor h1 { font-size: 1.6625rem; font-weight: 700; margin: 0.6em 0 0.3em; line-height: 1.25; }
        .notepad-editor h2 { font-size: 1.3625rem; font-weight: 700; margin: 0.6em 0 0.3em; line-height: 1.3; }
        .notepad-editor h3 { font-size: 1.1625rem; font-weight: 700; margin: 0.6em 0 0.3em; line-height: 1.35; }
        .notepad-editor p  { margin: 0.4em 0; }
        .notepad-editor ul, .notepad-editor ol { padding-left: 1.4em; margin: 0.4em 0; }
        .notepad-editor blockquote {
          border-left: 3px solid var(--pg-accent);
          padding: 0.1em 0.8em;
          margin: 0.5em 0;
          color: var(--pg-text-secondary);
          font-style: italic;
        }
        .notepad-editor pre {
          background: var(--pg-bg);
          border: 1px solid var(--pg-border);
          border-radius: 6px;
          padding: 8px 10px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 0.85em;
          overflow-x: auto;
        }
        .notepad-editor a { color: var(--pg-accent); text-decoration: underline; }
        .notepad-editor img { max-width: 100%; height: auto; }
      `}</style>
    </div>
  )
}

// ─── Tiny toolbar primitives ─────────────────────────

function ToolGroup({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>{children}</div>
}

function Sep() {
  return <span style={{ width: 1, height: 18, background: 'var(--pg-border)', margin: '0 2px' }} />
}

function ToolButton({
  children, title, onClick, active,
}: {
  children: React.ReactNode
  title: string
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active || undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      style={toolBtnBaseStyle(!!active)}
    >{children}</button>
  )
}

function toolBtnBaseStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    border: 'none',
    borderRadius: 4,
    background: active ? 'var(--color-accent-weak)' : 'transparent',
    color: active ? 'var(--pg-accent)' : 'var(--pg-text)',
    cursor: 'pointer',
  }
}
